import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Bell, AlertCircle, FileEdit, Scan, Clock, X } from 'lucide-react';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { ScrollArea } from '@/components/ui/scroll-area';

export interface Notification {
  id: string;
  type: 'urgent_email' | 'draft_ready' | 'imaging_alert';
  title: string;
  message: string;
  timestamp: string;
  read: boolean;
  caseId?: string;
  emailId?: string;
}

interface NotificationPanelProps {
  isOpen: boolean;
  onClose: () => void;
}

const notificationIcons: Record<string, React.ReactNode> = {
  urgent_email: <AlertCircle className="size-5 text-urgent" />,
  draft_ready: <FileEdit className="size-5 text-primary" />,
  imaging_alert: <Scan className="size-5 text-warning" />,
};

export function NotificationPanel({ isOpen, onClose }: NotificationPanelProps) {
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const wsRef = useRef<WebSocket | null>(null);
  const navigate = useNavigate();

  useEffect(() => {
    if (!isOpen) return;

    const wsProtocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
    const wsUrl = `${wsProtocol}//${window.location.host}/ws/notifications`;

    const ws = new WebSocket(wsUrl);
    wsRef.current = ws;

    ws.onopen = () => {
      console.log('WebSocket notifiche connesso');
    };

    ws.onmessage = (event) => {
      try {
        const data = JSON.parse(event.data);
        const newNotification: Notification = {
          id: `notif_${Date.now()}`,
          type: data.type,
          title: getNotificationTitle(data.type),
          message: getNotificationMessage(data),
          timestamp: data.timestamp || new Date().toISOString(),
          read: false,
          caseId: data.payload?.case_id,
          emailId: data.payload?.email_id,
        };
        setNotifications((prev) => [newNotification, ...prev]);
      } catch (e) {
        console.error('Errore parsing messaggio WebSocket:', e);
      }
    };

    ws.onerror = (error) => {
      console.error('WebSocket error:', error);
    };

    ws.onclose = () => {
      console.log('WebSocket notifiche disconnesso');
    };

    return () => {
      ws.close();
    };
  }, [isOpen]);

  const getNotificationTitle = (type: string): string => {
    switch (type) {
      case 'urgent_email':
        return 'Email Urgente';
      case 'draft_ready':
        return 'Draft Pronto';
      case 'imaging_alert':
        return 'Analisi Immagine';
      default:
        return 'Notifica';
    }
  };

  const getNotificationMessage = (data: any): string => {
    switch (data.type) {
      case 'urgent_email':
        return data.payload?.subject || 'Nuova email urgente ricevuta';
      case 'draft_ready':
        return 'Un draft di risposta è pronto per la revisione';
      case 'imaging_alert':
        return 'Analisi immagine con bassa accuratezza richiede revisione';
      default:
        return data.payload?.summary || '';
    }
  };

  const markAsRead = (id: string) => {
    setNotifications((prev) =>
      prev.map((n) => (n.id === id ? { ...n, read: true } : n))
    );
  };

  const markAllAsRead = () => {
    setNotifications((prev) => prev.map((n) => ({ ...n, read: true })));
  };

  const clearAll = () => {
    setNotifications([]);
  };

  const handleNotificationClick = (notif: Notification) => {
    markAsRead(notif.id);
    if (notif.caseId) {
      navigate(`/cases/${notif.caseId}`);
      onClose();
    }
  };

  const unreadCount = notifications.filter((n) => !n.read).length;

  return (
    <Sheet open={isOpen} onOpenChange={(open) => { if (!open) onClose(); }}>
      <SheetContent side="right" className="w-96 sm:max-w-md">
        <SheetHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
          <div className="flex items-center gap-2">
            <Bell className="size-5" />
            <SheetTitle>Notifiche</SheetTitle>
            {unreadCount > 0 && (
              <Badge variant="destructive" className="text-xs">{unreadCount}</Badge>
            )}
          </div>
          <div className="flex items-center gap-1">
            {notifications.length > 0 && (
              <>
                <Button variant="ghost" size="sm" onClick={markAllAsRead} className="h-7 text-xs">
                  Segna tutte lette
                </Button>
                <Button variant="ghost" size="sm" onClick={clearAll} className="h-7 text-xs">
                  Cancella
                </Button>
              </>
            )}
          </div>
        </SheetHeader>

        <ScrollArea className="h-[calc(100vh-5rem)] mt-4">
          {notifications.length > 0 ? (
            <div className="divide-y divide-border">
              {notifications.map((notif) => (
                <button
                  key={notif.id}
                  onClick={() => handleNotificationClick(notif)}
                  className={`w-full text-left p-4 hover:bg-muted/50 transition-colors ${
                    !notif.read ? 'bg-primary/5' : ''
                  }`}
                >
                  <div className="flex items-start gap-3">
                    <div className="mt-0.5">
                      {notificationIcons[notif.type] || <Bell className="size-5 text-muted-foreground" />}
                    </div>
                    <div className="flex-1">
                      <p className="text-sm font-medium text-foreground">{notif.title}</p>
                      <p className="text-sm text-muted-foreground mt-0.5">{notif.message}</p>
                      <p className="text-xs text-muted-foreground/60 mt-1.5 flex items-center gap-1">
                        <Clock className="size-3" />
                        {new Date(notif.timestamp).toLocaleString('it-IT')}
                      </p>
                    </div>
                    {!notif.read && (
                      <div className="size-2 bg-primary rounded-full mt-2 shrink-0" />
                    )}
                  </div>
                </button>
              ))}
            </div>
          ) : (
            <div className="flex flex-col items-center justify-center h-64 text-muted-foreground">
              <Bell className="size-12 mb-3 text-muted-foreground/30" />
              <p className="text-sm">Nessuna notifica</p>
            </div>
          )}
        </ScrollArea>
      </SheetContent>
    </Sheet>
  );
}
